import { useMutation, useQuery } from "@tanstack/react-query";

import type { MailSignatureData } from "@/components/email-composer/mail-signature";
import type { SendAsOwner, SendMailbox } from "@/components/email-composer/email-composer";
import { fetchApi } from "@/lib/api";

/** Who the deal composer sends as: the viewer's most connected mailbox on this deal's brand (Outlook, Gmail, or the
 * company's Google delegation), connected or not, and its live Gmail signature.
 * ``owner`` is the deal's shown owner, present only when the viewer may send as them — a deal with
 * no owner, or one the viewer may not send as, carries ``owner: null``. */
export interface DealEmailSender extends MailSignatureData {
  mailbox: SendMailbox;
  owner: SendAsOwner | null;
  /** The viewer may start Connect Outlook. */
  outlook_connect?: boolean;
}

export function useDealEmailSender(dealId: string, enabled: boolean) {
  return useQuery<DealEmailSender>({
    queryKey: ["dcc", "deal-email-sender", dealId],
    queryFn: () => fetchApi(`/deals/${dealId}/email-sender`),
    // A grant made in another tab shows when the person comes back.
    staleTime: 0,
    enabled,
  });
}

export interface StagedAttachment {
  staging_key: string;
  filename: string;
  content_type: string;
}

/** Stage one file for a deal email: the server signs a private PUT, and the browser sends the bytes straight to it,
 *  reporting 0–1 as they go. No bytes pass through the API. */
export async function stageDealEmailAttachment(
  dealId: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<StagedAttachment> {
  const content_type = file.type || "application/octet-stream";
  const mint: { staging_key: string; upload_url: string; upload_headers: Record<string, string> } = await fetchApi(
    `/deals/${dealId}/email-attachment-upload-url`,
    { method: "POST", body: JSON.stringify({ filename: file.name, content_type }) },
  );
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", mint.upload_url);
    // Signed into the URL: the storage bucket refuses a PUT without exactly these.
    for (const [k, v] of Object.entries(mint.upload_headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new Error(`${file.name} did not upload`));
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`${file.name} did not upload`));
    xhr.send(file);
  });
  onProgress(1);
  return { staging_key: mint.staging_key, filename: file.name, content_type };
}

export interface SendDealEmailInput {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  /** Gmail's thread id on a reply; empty for a new email. */
  thread_id: string;
  attachments: StagedAttachment[];
  /** One per compose, replayed unchanged on Try Again so the server checks Sent instead of sending twice. */
  client_token: string;
  /** "owner": send from the deal's shown owner's mailbox. Re-checked server-side on every call. */
  send_as?: "me" | "owner";
  /** The owner's email the composer showed and the user confirmed sending as. The server refuses
   * if the deal's owner changed since. Ignored when `send_as !== "owner"`. */
  owner_email?: string;
}

export interface SendDealEmailResult {
  id: string;
  status: "preparing" | "sending" | "sent" | "failed";
  message_id: string | null;
  thread_id: string | null;
  error: string | null;
  contact_id: string | null;
}

export function useSendDealEmail(dealId: string) {
  return useMutation<SendDealEmailResult, Error, SendDealEmailInput>({
    mutationFn: (input) => fetchApi(`/deals/${dealId}/emails`, { method: "POST", body: JSON.stringify(input) }),
  });
}
